/**
 * ASG header behaviour — port of SiteHeader.vue + MobileMenu.vue.
 *
 * Three independent concerns:
 *   1. Sticky scroll-hide  — drop the bar past 100px of downward scroll, bring it
 *                            back on any upward scroll.
 *   2. Mega panels         — chevron toggles a panel; only one open at a time.
 *   3. Mobile drawer       — full-screen, with the body scroll locked behind it.
 *
 * Written as a custom element so the Shopify theme editor, which tears down and
 * re-inserts a section's DOM on every settings change, re-runs setup for free.
 */
class AsgHeader extends HTMLElement {
  connectedCallback() {
    this.header = this.querySelector('.asg-header');
    this.overlay = this.querySelector('[data-asg-overlay]');
    this.mobile = this.querySelector('#asg-mobile-menu');
    this.burger = this.querySelector('[data-asg-burger]');

    this.openPanelId = null;
    this.lastScrollY = window.scrollY;
    this.ticking = false;

    this.bindPanels();
    this.bindMobile();

    if (this.dataset.sticky === 'true') {
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }

    this.onKeydown = this.onKeydown.bind(this);
    document.addEventListener('keydown', this.onKeydown);

    this.onDocumentClick = this.onDocumentClick.bind(this);
    document.addEventListener('click', this.onDocumentClick);
  }

  disconnectedCallback() {
    if (this.closeTimer) clearTimeout(this.closeTimer);
    if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('click', this.onDocumentClick);
    this.unlockScroll();
  }

  /* ---------------------------------------------------------------- panels */

  bindPanels() {
    this.triggers = Array.from(this.querySelectorAll('[data-asg-panel-trigger]'));

    this.triggers.forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const id = trigger.dataset.asgPanelTrigger;
        if (this.openPanelId === id) {
          this.closePanels();
        } else {
          this.openPanel(id);
        }
      });
    });

    // Hover open/close, matching the main site. Pointer hover only — on touch
    // devices matchMedia('(hover: hover)') is false and the chevron click above
    // remains the way in. A small close delay forgives the diagonal move from
    // the trigger down into the panel; without it the panel slams shut the
    // moment the pointer crosses the gap between nav item and panel.
    if (!window.matchMedia('(hover: hover)').matches) return;

    const cancelClose = () => {
      if (this.closeTimer) {
        clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }
    };
    const scheduleClose = () => {
      cancelClose();
      this.closeTimer = setTimeout(() => this.closePanels(), 150);
    };

    this.querySelectorAll('.asg-nav__item').forEach((item) => {
      const trigger = item.querySelector('[data-asg-panel-trigger]');
      if (!trigger) return;
      item.addEventListener('mouseenter', () => {
        cancelClose();
        this.openPanel(trigger.dataset.asgPanelTrigger);
      });
      item.addEventListener('mouseleave', scheduleClose);
    });

    this.querySelectorAll('[data-asg-panel]').forEach((panel) => {
      panel.addEventListener('mouseenter', cancelClose);
      panel.addEventListener('mouseleave', scheduleClose);
    });
  }

  openPanel(id) {
    this.closePanels();

    const panel = this.querySelector(`[data-asg-panel="${id}"]`);
    const trigger = this.querySelector(`[data-asg-panel-trigger="${id}"]`);
    if (!panel || !trigger) return;

    panel.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    trigger.closest('.asg-nav__item')?.classList.add('is-open');
    if (this.overlay) this.overlay.hidden = false;
    this.openPanelId = id;
  }

  closePanels() {
    this.querySelectorAll('[data-asg-panel]').forEach((panel) => {
      panel.hidden = true;
    });
    this.triggers?.forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
      trigger.closest('.asg-nav__item')?.classList.remove('is-open');
    });
    if (this.overlay) this.overlay.hidden = true;
    this.openPanelId = null;
  }

  /* ---------------------------------------------------------------- mobile */

  bindMobile() {
    if (!this.burger || !this.mobile) return;

    this.burger.addEventListener('click', (event) => {
      event.stopPropagation();
      this.mobile.hidden ? this.openMobile() : this.closeMobile();
    });

    this.querySelector('[data-asg-mobile-close]')?.addEventListener('click', () => this.closeMobile());
  }

  openMobile() {
    this.closePanels();
    this.mobile.hidden = false;
    this.burger.setAttribute('aria-expanded', 'true');
    this.lockScroll();
    // Send focus into the drawer so the next Tab lands inside it rather than
    // continuing through the page behind.
    this.mobile.querySelector('a, button')?.focus();
  }

  closeMobile() {
    this.mobile.hidden = true;
    this.burger.setAttribute('aria-expanded', 'false');
    this.unlockScroll();
    this.burger.focus();
  }

  lockScroll() {
    this.scrollLocked = true;
    document.body.style.overflow = 'hidden';
  }

  unlockScroll() {
    if (!this.scrollLocked) return;
    this.scrollLocked = false;
    document.body.style.overflow = '';
  }

  /* ---------------------------------------------------------------- global */

  onKeydown(event) {
    if (event.key !== 'Escape') return;
    if (this.mobile && !this.mobile.hidden) {
      this.closeMobile();
    } else if (this.openPanelId) {
      this.querySelector(`[data-asg-panel-trigger="${this.openPanelId}"]`)?.focus();
      this.closePanels();
    }
  }

  onDocumentClick(event) {
    if (!this.openPanelId) return;
    // A click inside the open panel (or on its trigger) is not a dismissal.
    if (event.target.closest('.asg-mega') || event.target.closest('[data-asg-panel-trigger]')) return;
    this.closePanels();
  }

  onScroll() {
    if (this.ticking) return;
    this.ticking = true;

    window.requestAnimationFrame(() => {
      const y = window.scrollY;
      const goingDown = y > this.lastScrollY;

      // Never hide the bar while a panel or the drawer is open — the user is
      // mid-interaction and pulling it out from under them loses their place.
      const busy = this.openPanelId !== null || (this.mobile && !this.mobile.hidden);

      if (goingDown && y > 100 && !busy) {
        this.classList.add('is-hidden');
      } else if (!goingDown) {
        this.classList.remove('is-hidden');
      }

      this.lastScrollY = y;
      this.ticking = false;
    });
  }
}

if (!customElements.get('asg-header')) {
  customElements.define('asg-header', AsgHeader);
}
